import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  useGetNotificationLogQuery,
  useGetNotificationLogRecipientsQuery,
} from "../../store/services/notification-logs";
import {
  NotificationLogRecipientItem,
  NotificationRecipientStatus,
} from "../../models/notification-logs";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { emptyMessage } from "../../components/ui/EmptyState";
import { formatSupportDateTimeMinutes } from "../../utils/functions/parseInstant";
import {
  notificationSenderLabel,
  notificationStatusLabel,
  notificationStatusTone,
  notificationTypeLabel,
  recipientStatusLabel,
  recipientStatusTone,
} from "./notificationLogPresentation";

const statusOptions: { label: string; value: NotificationRecipientStatus | null }[] = [
  { label: "Todos", value: null },
  { label: "Enviada", value: "SENT" },
  { label: "Sin dispositivo", value: "NO_TOKEN" },
  { label: "Falló", value: "FAILED" },
];

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-column gap-1 mb-3">
    <span className="text-600 text-sm">{label}</span>
    <span>{children}</span>
  </div>
);

export const PushNotificationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [status, setStatus] = useState<NotificationRecipientStatus | null>(null);

  const { data: notification, isLoading } = useGetNotificationLogQuery(id ?? "", { skip: !id });
  const {
    data: recipients,
    isLoading: isLoadingRecipients,
    isFetching,
  } = useGetNotificationLogRecipientsQuery(
    { id: id ?? "", page, limit, ...(status ? { status } : {}) },
    { skip: !id }
  );

  if (isLoading) {
    return (
      <div className="gap-4 flex flex-column justify-content-center align-items-center p-3 min-h-11rem p-8">
        <ProgressSpinner />
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="p-card p-5 text-center">
        <h2>Notificación no encontrada</h2>
        <Button
          label="Volver"
          icon="pi pi-arrow-left"
          onClick={() => navigate("/notifications/history")}
        />
      </div>
    );
  }

  const handlePage = (event: DataTablePageEvent) => {
    setPage((event.page ?? 0) + 1);
    setLimit(event.rows);
  };

  return (
    <div className="p-3">
      <Breadcrumb
        items={[
          { label: "Historial de notificaciones", to: "/notifications/history" },
          { label: notification.title },
        ]}
      />

      <Panel header="Notificación" className="mb-4">
        <div className="grid">
          <div className="col-12 md:col-6">
            <Field label="Título">{notification.title}</Field>
            <Field label="Mensaje">{notification.body}</Field>
            <Field label="Fecha y hora">
              {formatSupportDateTimeMinutes(notification.sentAt)}
            </Field>
          </div>
          <div className="col-12 md:col-6">
            <Field label="Tipo">
              <StatusBadge
                label={notificationTypeLabel(notification)}
                tone={notification.type === "MANUAL" ? "info" : "pending"}
              />
            </Field>
            <Field label="Enviada por">{notificationSenderLabel(notification)}</Field>
            <Field label="Resultado">
              <StatusBadge
                label={notificationStatusLabel(notification.status)}
                tone={notificationStatusTone(notification.status)}
              />
            </Field>
            <Field label="Destinatarios">{notification.recipientsCount}</Field>
          </div>
        </div>

        {notification.segmentCriteria && (
          <Field label="Criterio de segmentación">
            <pre className="m-0 text-sm surface-100 p-3 border-round overflow-auto">
              {JSON.stringify(notification.segmentCriteria, null, 2)}
            </pre>
          </Field>
        )}
      </Panel>

      <Panel header="Destinatarios">
        <div className="flex flex-column gap-2 mb-3" style={{ maxWidth: "16rem" }}>
          <label htmlFor="recipientStatus" className="text-600 text-sm">
            Resultado
          </label>
          <Dropdown
            inputId="recipientStatus"
            value={status}
            options={statusOptions}
            onChange={(event) => {
              setStatus(event.value);
              setPage(1);
            }}
            placeholder="Todos"
          />
        </div>

        <DataTable
          value={recipients?.items ?? []}
          loading={isLoadingRecipients || isFetching}
          lazy
          paginator
          first={(page - 1) * limit}
          rows={limit}
          totalRecords={recipients?.total ?? 0}
          rowsPerPageOptions={[25, 50, 100]}
          onPage={handlePage}
          dataKey="id"
          responsiveLayout="scroll"
          emptyMessage={emptyMessage("destinatarios", status !== null)}
        >
          <Column
            header="Usuario"
            body={(row: NotificationLogRecipientItem) =>
              row.userName || `Usuario #${row.userId}`
            }
            style={{ minWidth: "14rem" }}
          />
          <Column
            header="Email"
            body={(row: NotificationLogRecipientItem) => row.userEmail || "—"}
            style={{ minWidth: "16rem" }}
          />
          <Column
            header="Resultado"
            body={(row: NotificationLogRecipientItem) => (
              <StatusBadge
                label={recipientStatusLabel(row.status)}
                tone={recipientStatusTone(row.status)}
              />
            )}
            style={{ width: "11rem" }}
          />
          <Column
            header="Detalle"
            body={(row: NotificationLogRecipientItem) => row.failureReason || "—"}
            style={{ minWidth: "14rem" }}
          />
        </DataTable>
      </Panel>
    </div>
  );
};
